import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, ListGroup, Spinner, Alert, Badge, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { fetchTaskComments, addTaskComment, deleteTaskComment, TaskComment } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';

interface TaskCommentsProps {
  taskId: number;
}

const TaskComments: React.FC<TaskCommentsProps> = ({ taskId }) => {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState<string>('');
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [postError, setPostError] = useState<string | null>(null);
  
  // Get user from the useAuth hook
  const { user } = useAuth();

  // Fetch comments function
  const loadComments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // TODO: Implement pagination later if needed
      const response = await fetchTaskComments(taskId, 1, 50); // Fetch up to 50 comments for now
      setComments(response.items);
    } catch (err: any) {
      console.error('Error loading comments:', err);
      setError(err.message || 'Failed to load comments.');
    } finally {
      setIsLoading(false);
    }
  }, [taskId]);

  // Load comments on mount
  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // Handle new comment submission
  const handlePostComment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newComment.trim() || !user) return; // Also check if user is logged in

    setIsPosting(true);
    setPostError(null);
    try {
      const postedComment = await addTaskComment(taskId, newComment);
      // Manually add user info as the backend response might not include nested author details immediately
      // Alternatively, refetch comments after posting.
      setComments(prevComments => [{
          ...postedComment, 
          // Ensure user and user.name exist before accessing
          author: { id: user.id, name: user.name ?? 'You' } 
      }, ...prevComments]); 
      setNewComment(''); // Clear input
    } catch (err: any) {
      console.error('Error posting comment:', err);
      setPostError(err.message || 'Failed to post comment.');
    } finally {
      setIsPosting(false);
    }
  };

  // Handle comment deletion
  const handleDeleteComment = async (commentId: number) => {
    // Optional: Add confirmation dialog here
    // if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await deleteTaskComment(commentId);
      setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      // Show error to user (e.g., using a toast notification or inline alert)
      alert(`Error deleting comment: ${err.message || 'Please try again.'}`);
    }
  };

  return (
    <div className="comments-section mt-3">
      <h6 className="fw-bold mb-3">Comments <Badge bg="secondary" pill>{comments.length}</Badge></h6>

      {/* New Comment Form */}
      <Form onSubmit={handlePostComment} className="mb-3">
        <InputGroup>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder={user ? "Add a comment..." : "Log in to comment"}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            required
            disabled={!user || isPosting} // Disable if not logged in or currently posting
          />
          <Button type="submit" variant="primary" disabled={!user || isPosting || !newComment.trim()}>
            {isPosting ? 
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 
              <FontAwesomeIcon icon={faPaperPlane} />
            }
          </Button>
        </InputGroup>
        {postError && <Alert variant="danger" className="mt-2 p-2 small">{postError}</Alert>}
      </Form>

      {/* Comments List */}
      {isLoading && (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
          <p className="mb-0 text-muted small">Loading comments...</p>
        </div>
      )}
      {error && <Alert variant="warning" className="p-2 small">{error}</Alert>}
      {!isLoading && !error && (
        <ListGroup variant="flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {comments.length === 0 ? (
            <ListGroup.Item className="text-muted text-center small border-0 px-0 py-3">
              No comments yet.
            </ListGroup.Item>
          ) : (
            comments.map(comment => (
              <ListGroup.Item key={comment.id} className="d-flex border-0 border-bottom px-0">
                {/* Basic Avatar Placeholder */}
                <div className="flex-shrink-0 me-2">
                  <div style={{
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      backgroundColor: '#e9ecef', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: 'bold', 
                      fontSize: '0.8rem'
                    }}>
                    {/* Use optional chaining for safety */}
                    {comment.author?.name?.substring(0, 1).toUpperCase() || 'U'}
                  </div>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center">
                      <div>
                          <strong className="me-2">{comment.author?.name || 'Unknown User'}</strong>
                          <small className="text-muted">{formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}</small>
                      </div>
                      {/* Show delete button only if current user is the author */} 
                      {user && user.id === comment.authorId && (
                          <Button 
                              variant="link" 
                              size="sm" 
                              className="text-danger p-0" 
                              onClick={() => handleDeleteComment(comment.id)}
                              title="Delete comment"
                          >
                              <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                      )}
                  </div>
                  <p className="mb-0 small mt-1">{comment.content}</p>
                </div>
              </ListGroup.Item>
            ))
          )}
        </ListGroup>
      )}
    </div>
  );
};

export default TaskComments; 